import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function PwdInput({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} className="h-11 rounded-xl pr-10" data-testid={testid} />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700" data-testid={`${testid}-eye`} aria-label="Mostrar u ocultar contraseña">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
    </div>
  );
}

function CreateModal({ kind, refId, name, defaultEmail, onClose, onDone }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [pwd, setPwd] = useState(""); const [confirm, setConfirm] = useState(""); const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!EMAIL_RE.test(email.trim())) { toast.error("Email con formato inválido"); return; }
    if (pwd.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    if (pwd !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setSaving(true);
    try {
      const body = { email: email.trim().toLowerCase(), name: name || email.trim(), password: pwd, role: kind };
      if (kind === "consignador") body.owner_id = refId; else body.investor_id = refId;
      await adminApi.post("/admin/users", body);
      toast.success("Acceso al portal creado"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear el acceso"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pa-create-dialog">
        <DialogHeader><DialogTitle>Crear acceso al portal</DialogTitle><DialogDescription>Se cifra con el mismo mecanismo seguro del login. La contraseña nunca se guarda en texto plano.</DialogDescription></DialogHeader>
        <label className="text-xs text-zinc-500">Email / usuario de acceso</label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl" data-testid="pa-create-email" />
        <label className="text-xs text-zinc-500">Contraseña</label><PwdInput value={pwd} onChange={(e) => setPwd(e.target.value)} testid="pa-create-pwd" />
        <label className="text-xs text-zinc-500">Confirmar contraseña</label><PwdInput value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="pa-create-confirm" />
        <Button onClick={submit} disabled={saving} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 mt-1" data-testid="pa-create-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear acceso"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function EmailModal({ email, onClose, onDone }) {
  const [v, setV] = useState(email); const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!EMAIL_RE.test(v.trim())) { toast.error("Email con formato inválido"); return; }
    setSaving(true);
    try { await adminApi.put(`/admin/users/${encodeURIComponent(email)}`, { email: v.trim().toLowerCase() }); toast.success("Email de acceso actualizado"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pa-email-dialog">
        <DialogHeader><DialogTitle>Editar email de acceso</DialogTitle><DialogDescription>Conserva la misma cuenta/identidad; solo cambia el login. No afecta datos ni saldos.</DialogDescription></DialogHeader>
        <label className="text-xs text-zinc-500">Nuevo email</label>
        <Input type="email" value={v} onChange={(e) => setV(e.target.value)} className="h-11 rounded-xl" data-testid="pa-email-input" />
        <Button onClick={submit} disabled={saving} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 mt-1" data-testid="pa-email-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar email"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function ResetModal({ email, onClose, onDone }) {
  const [pwd, setPwd] = useState(""); const [confirm, setConfirm] = useState(""); const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (pwd.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    if (pwd !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setSaving(true);
    try { await adminApi.put(`/admin/users/${encodeURIComponent(email)}`, { password: pwd }); toast.success("Contraseña restablecida"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo restablecer"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pa-reset-dialog">
        <DialogHeader><DialogTitle>Restablecer contraseña</DialogTitle><DialogDescription>No se muestra la contraseña actual. Al guardar, la anterior deja de funcionar y se cierran las sesiones activas de esa cuenta.</DialogDescription></DialogHeader>
        <p className="text-xs text-zinc-400">Usuario: <b>{email}</b></p>
        <label className="text-xs text-zinc-500">Nueva contraseña</label><PwdInput value={pwd} onChange={(e) => setPwd(e.target.value)} testid="pa-reset-pwd" />
        <label className="text-xs text-zinc-500">Confirmar contraseña</label><PwdInput value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="pa-reset-confirm" />
        <Button onClick={submit} disabled={saving} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 mt-1" data-testid="pa-reset-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar nueva contraseña"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export function PortalAccessManager({ kind, refId, name, defaultEmail }) {
  const [account, setAccount] = useState(null); const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/users");
      const match = (r.data || []).find((u) => (kind === "consignador" ? u.owner_id === refId : u.investor_id === refId));
      setAccount(match || null);
    } catch { setAccount(null); } finally { setLoading(false); }
  }, [kind, refId]);
  useEffect(() => { load(); }, [load]);
  const done = () => { setModal(null); load(); };
  const label = kind === "consignador" ? "consignador" : "inversionista";
  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3" data-testid="portal-access">
      <p className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Acceso al portal</p>
      {loading ? <Loader2 className="w-4 h-4 animate-spin text-zinc-400 mt-2" /> : account ? (
        <div className="mt-1.5 space-y-2">
          <div>
            <p className="text-sm font-semibold text-emerald-700" data-testid="pa-status">{account.active === false ? "Acceso deshabilitado" : "Acceso habilitado"}</p>
            <p className="text-xs text-zinc-600" data-testid="pa-email">{account.email}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setModal("email")} className="h-9 rounded-lg text-xs" data-testid="pa-edit-email-btn">Editar email</Button>
            <Button size="sm" variant="outline" onClick={() => setModal("reset")} className="h-9 rounded-lg text-xs" data-testid="pa-reset-btn">Restablecer contraseña</Button>
          </div>
        </div>
      ) : (
        <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-sm text-zinc-500" data-testid="pa-status">Este {label} todavía no tiene acceso al portal.</p>
          <Button size="sm" onClick={() => setModal("create")} className="h-9 rounded-lg bg-zinc-900 hover:bg-zinc-800" data-testid="pa-create-btn">Crear acceso al portal</Button>
        </div>
      )}
      {modal === "create" && <CreateModal kind={kind} refId={refId} name={name} defaultEmail={defaultEmail} onClose={() => setModal(null)} onDone={done} />}
      {modal === "email" && account && <EmailModal email={account.email} onClose={() => setModal(null)} onDone={done} />}
      {modal === "reset" && account && <ResetModal email={account.email} onClose={() => setModal(null)} onDone={done} />}
    </div>
  );
}
